import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader } from "../components/AppHeader";
import { EmptyState, MoneyStat } from "../components/MoneyStat";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Separator } from "../components/ui/separator";
import { StatusBadge, STATUS_ORCAMENTO_LABEL, STATUS_ORCAMENTO_OPCOES } from "../components/StatusBadge";
import { StatusSelect } from "../components/StatusSelect";
import { api, erroMsg } from "../lib/api";
import { brl, dataHora, dataSomente, pct } from "../lib/format";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import {
  CalendarClock,
  Eye,
  FileDown,
  FileText,
  Pencil,
  RotateCcw,
  Search,
  Target,
  Trash2,
} from "lucide-react";

const acompanhamentoVazio = { data_retorno: "", nota_acompanhamento: "" };

/** Monta um texto legível para um item do histórico de alterações do orçamento. */
const descreverEvento = (ev) => {
  if (ev.evento === "criado") return "Orçamento criado";
  const partes = [];
  if (ev.status_novo) {
    partes.push(
      `Status alterado de "${STATUS_ORCAMENTO_LABEL[ev.status_anterior] || ev.status_anterior}" para "${
        STATUS_ORCAMENTO_LABEL[ev.status_novo] || ev.status_novo
      }"`
    );
  }
  if (ev.motivo_perda) partes.push(`Motivo da perda: ${ev.motivo_perda}`);
  if (ev.data_retorno) partes.push(`Retorno agendado para ${dataSomente(ev.data_retorno)}`);
  if (ev.nota_acompanhamento) partes.push(`Observação: "${ev.nota_acompanhamento}"`);
  return partes.length ? partes.join(" • ") : "Acompanhamento atualizado";
};

export default function Orcamentos() {
  const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [carregando, setCarregando] = useState(true);
  const [excluir, setExcluir] = useState(null);
  const [visualizar, setVisualizar] = useState(null);
  const [stats, setStats] = useState(null);
  const [motivoDialog, setMotivoDialog] = useState({ aberto: false, quote: null, motivo: "" });
  const [acompanhamento, setAcompanhamento] = useState(acompanhamentoVazio);
  const [salvandoAcompanhamento, setSalvandoAcompanhamento] = useState(false);
  const { carregarDeOrcamento, editandoId, sairModoEdicao } = useCart();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get("/quotes", {
        params: { q: busca, status_filtro: statusFiltro },
      });
      setItens(r.data.itens);
    } catch (e) {
      toast.error(erroMsg(e, "Não foi possível carregar os orçamentos."));
    } finally {
      setCarregando(false);
    }
  }, [busca, statusFiltro]);

  const carregarStats = useCallback(async () => {
    try {
      const r = await api.get("/quotes/stats");
      setStats(r.data);
    } catch {
      // indicadores são um extra — falha silenciosa não deve travar a página
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(carregar, 250);
    return () => clearTimeout(t);
  }, [carregar]);

  useEffect(() => {
    carregarStats();
  }, [carregarStats]);

  useEffect(() => {
    if (visualizar) {
      setAcompanhamento({
        data_retorno: visualizar.data_retorno || "",
        nota_acompanhamento: visualizar.nota_acompanhamento || "",
      });
    }
  }, [visualizar]);

  const abrirPdf = async (q) => {
    try {
      const r = await api.get(`/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("Não foi possível gerar o PDF.");
    }
  };

  const editarOrcamento = (q) => {
    carregarDeOrcamento(q, { editar: true });
    toast.success(`Editando o orçamento ${q.numero}`, {

      description: "Altere os itens e clique em Salvar alterações.",
    });
    navigate("/orcamento");
  };

  const confirmarExclusao = async () => {
    try {
      await api.delete(`/quotes/${excluir.id}`);
      toast.success(`Orçamento ${excluir.numero} excluído`);
      if (editandoId === excluir.id) sairModoEdicao();
      if (visualizar?.id === excluir.id) setVisualizar(null);
      setExcluir(null);
      carregar();
      carregarStats();
    } catch (e) {
      toast.error(erroMsg(e));
    }
  };

  const atualizarNaLista = (atualizado) => {
    setItens((prev) => prev.map((it) => (it.id === atualizado.id ? atualizado : it)));
    setVisualizar((prev) => (prev?.id === atualizado.id ? atualizado : prev));
  };

  const mudarStatus = async (q, novoStatus, motivoPerda) => {
    try {
      const payload = { status: novoStatus };
      if (motivoPerda !== undefined) payload.motivo_perda = motivoPerda;
      const r = await api.patch(`/quotes/${q.id}/status`, payload);
      atualizarNaLista(r.data);
      toast.success(`Orçamento ${q.numero} marcado como "${STATUS_ORCAMENTO_LABEL[novoStatus]}"`);
      carregarStats();
    } catch (e) {
      toast.error(erroMsg(e, "Não foi possível atualizar o status."));
    }
  };

  const handleStatusChange = (q, novoStatus) => {
    if (novoStatus === "PERDIDO") {
      setMotivoDialog({ aberto: true, quote: q, motivo: "" });
    } else {
      mudarStatus(q, novoStatus);
    }
  };

  const confirmarPerdido = () => {
    mudarStatus(motivoDialog.quote, "PERDIDO", motivoDialog.motivo.trim());
    setMotivoDialog({ aberto: false, quote: null, motivo: "" });
  };

  const salvarAcompanhamento = async () => {
    if (!visualizar) return;
    setSalvandoAcompanhamento(true);
    try {
      const r = await api.patch(`/quotes/${visualizar.id}/status`, {
        data_retorno: acompanhamento.data_retorno || null,
        nota_acompanhamento: acompanhamento.nota_acompanhamento,
      });
      atualizarNaLista(r.data);
      toast.success("Acompanhamento salvo");
    } catch (e) {
      toast.error(erroMsg(e, "Não foi possível salvar o acompanhamento."));
    } finally {
      setSalvandoAcompanhamento(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-10">
      <AppHeader />
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h1 className="font-display text-[20px] font-bold tracking-tight text-slate-900">
              Histórico de orçamentos
            </h1>
            <p className="text-[12.5px] text-slate-600">
              Acompanhe o funil de vendas, reabra, reutilize ou compartilhe orçamentos já gerados.
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Select value={statusFiltro} onValueChange={setStatusFiltro}>
              <SelectTrigger
                className="h-11 w-full bg-white sm:w-[180px]"
                data-testid="orcamentos-filtro-status"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="todos">Todos os status</SelectItem>
                {STATUS_ORCAMENTO_OPCOES.map((o) => (
                  <SelectItem key={o.codigo} value={o.codigo}>
                    {o.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-full sm:w-80">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por cliente ou número…"
                className="h-11 bg-white pl-9"
                data-testid="orcamentos-busca-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-3 py-5 sm:px-6">
        {stats && (
          <div className="mb-5 space-y-3" data-testid="orcamentos-funil-stats">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <MoneyStat
                label="Enviados"
                valor={String(stats.por_status?.ENVIADO ?? 0)}
                testid="stats-enviados"
              />
              <MoneyStat
                label="Em negociação"
                valor={String(stats.por_status?.EM_NEGOCIACAO ?? 0)}
                testid="stats-negociacao"
              />
              <MoneyStat
                label="Ganhos"
                valor={String(stats.por_status?.GANHO ?? 0)}
                sub={brl(stats.valor_por_status?.GANHO)}
                testid="stats-ganhos"
              />
              <MoneyStat
                label="Perdidos"
                valor={String(stats.por_status?.PERDIDO ?? 0)}
                testid="stats-perdidos"
              />
              <MoneyStat
                label="Taxa de conversão"
                valor={stats.taxa_conversao != null ? pct(stats.taxa_conversao) : "—"}
                sub="ganhos ÷ (ganhos + perdidos)"
                destaque
                testid="stats-conversao"
              />
            </div>

            {isAdmin && stats.por_representante && (
              <div
                className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                data-testid="orcamentos-stats-por-representante"
              >
                <div className="flex items-center gap-1.5 border-b border-slate-200 px-4 py-2.5">
                  <Target className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Funil por representante
                  </span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Representante</TableHead>
                      {STATUS_ORCAMENTO_OPCOES.map((o) => (
                        <TableHead key={o.codigo} className="text-right">
                          {o.nome}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(stats.por_representante).map(([nome, porStatus]) => (
                      <TableRow key={nome}>
                        <TableCell className="font-medium">{nome}</TableCell>
                        {STATUS_ORCAMENTO_OPCOES.map((o) => (
                          <TableCell key={o.codigo} className="num text-right">
                            {porStatus[o.codigo] ?? 0}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {carregando ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : itens.length === 0 ? (
          <EmptyState
            icone={FileText}
            titulo="Nenhum orçamento salvo"
            descricao="Monte um orçamento na consulta e clique em Salvar para que ele apareça aqui."
            acao={
              <Button onClick={() => navigate("/consulta")} data-testid="orcamentos-ir-consulta">
                Ir para a consulta
              </Button>
            }
          />
        ) : (
          <div className="space-y-3" data-testid="orcamentos-list">
            {itens.map((q) => (
              <div
                key={q.id}
                className="rounded-xl border border-slate-200 bg-white p-4"
                data-testid={`orcamento-card-${q.numero}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="bg-slate-900 font-mono-num text-white">{q.numero}</Badge>
                      <span className="font-display text-[15px] font-semibold text-slate-900">
                        {q.cliente_nome || "Cliente não informado"}
                      </span>
                      {editandoId === q.id && (
                        <Badge
                          className="bg-amber-100 text-[10px] uppercase text-amber-900"
                          data-testid={`orcamentos-editando-badge-${q.numero}`}
                        >
                          Em edição
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-500">
                      <span>{dataHora(q.criado_em)}</span>
                      <span>
                        {q.uf_nome} • {q.enquadramento_nome}
                      </span>
                      <span>Descontos por item</span>
                      <span>
                        {q.itens?.length || 0} item{(q.itens?.length || 0) === 1 ? "" : "s"}
                      </span>
                      <span>Vendedor: {q.user_nome}</span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <StatusSelect
                        value={q.status}
                        onValueChange={(v) => handleStatusChange(q, v)}
                        testid={`orcamentos-status-select-${q.numero}`}
                      />
                      {q.data_retorno && (
                        <span
                          className="inline-flex items-center gap-1 rounded-md bg-[#EEF2F9] px-2 py-1 text-[11px] font-medium text-[#1B315E]"
                          data-testid={`orcamentos-retorno-${q.numero}`}
                        >
                          <CalendarClock className="h-3 w-3" /> Retornar em{" "}
                          {dataSomente(q.data_retorno)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
                      Total
                    </div>
                    <div className="num font-display text-[22px] font-bold text-slate-900">
                      {brl(q.totais?.total_geral)}
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setVisualizar(q)}
                    data-testid={`orcamentos-visualizar-${q.numero}`}
                  >
                    <Eye className="mr-1.5 h-4 w-4" /> Visualizar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => editarOrcamento(q)}
                    data-testid={`orcamentos-editar-${q.numero}`}
                  >
                    <Pencil className="mr-1.5 h-4 w-4" /> Editar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      carregarDeOrcamento(q);
                      toast.success("Itens carregados em um novo orçamento");
                      navigate("/orcamento");
                    }}
                    data-testid={`orcamentos-reutilizar-${q.numero}`}
                  >
                    <RotateCcw className="mr-1.5 h-4 w-4" /> Reutilizar
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => abrirPdf(q)}
                    data-testid={`orcamentos-pdf-${q.numero}`}
                  >
                    <FileDown className="mr-1.5 h-4 w-4" /> PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:bg-red-50"
                    onClick={() => setExcluir(q)}
                    data-testid={`orcamentos-excluir-${q.numero}`}
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" /> Excluir
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Sheet open={!!visualizar} onOpenChange={(o) => !o && setVisualizar(null)}>
        <SheetContent
          className="w-full overflow-y-auto bg-white sm:max-w-xl"
          data-testid="orcamento-visualizar-drawer"
        >
          <SheetHeader>
            <SheetTitle className="flex flex-wrap items-center gap-2">
              <Badge className="bg-slate-900 font-mono-num text-white">
                {visualizar?.numero}
              </Badge>
              {visualizar?.cliente_nome || "Cliente não informado"}
              <StatusBadge status={visualizar?.status} />
              {editandoId === visualizar?.id && (
                <Badge className="bg-amber-100 text-[10px] uppercase text-amber-900">
                  Em edição
                </Badge>
              )}
            </SheetTitle>
            <SheetDescription>
              Criado em {dataHora(visualizar?.criado_em)} por {visualizar?.user_nome} •{" "}
              {visualizar?.uf_nome} • {visualizar?.enquadramento_nome}
            </SheetDescription>
          </SheetHeader>

          {visualizar && (
            <div className="mt-5 space-y-5 pb-8">
              <div
                className="space-y-3 rounded-lg border border-slate-200 p-3"
                data-testid="orcamento-visualizar-acompanhamento"
              >
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Status e acompanhamento
                </div>
                <StatusSelect
                  value={visualizar.status}
                  onValueChange={(v) => handleStatusChange(visualizar, v)}
                  className="h-10 w-full"
                  testid="orcamento-visualizar-status-select"
                />
                {visualizar.status === "PERDIDO" && visualizar.motivo_perda && (
                  <p className="rounded-md bg-rose-50 px-2.5 py-1.5 text-[12px] text-rose-800">
                    Motivo da perda: {visualizar.motivo_perda}
                  </p>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="acomp-data">Retornar em</Label>
                  <Input
                    id="acomp-data"
                    type="date"
                    value={acompanhamento.data_retorno}
                    onChange={(e) =>
                      setAcompanhamento((a) => ({ ...a, data_retorno: e.target.value }))
                    }
                    className="h-10"
                    data-testid="orcamento-visualizar-data-retorno-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="acomp-nota">Observação de acompanhamento</Label>
                  <Textarea
                    id="acomp-nota"
                    value={acompanhamento.nota_acompanhamento}
                    onChange={(e) =>
                      setAcompanhamento((a) => ({ ...a, nota_acompanhamento: e.target.value }))
                    }
                    placeholder="Ex.: Cliente vai decidir semana que vem"
                    rows={2}
                    data-testid="orcamento-visualizar-nota-input"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={salvarAcompanhamento}
                  disabled={salvandoAcompanhamento}
                  data-testid="orcamento-visualizar-salvar-acompanhamento-button"
                >
                  {salvandoAcompanhamento ? "Salvando…" : "Salvar acompanhamento"}
                </Button>
              </div>

              {(visualizar.cliente_cnpj ||
                visualizar.cliente_contato ||
                visualizar.condicao_pagamento ||
                visualizar.prazo_entrega ||
                visualizar.observacoes) && (
                <div
                  className="space-y-1.5 rounded-lg border border-slate-200 p-3"
                  data-testid="orcamento-visualizar-dados-cliente"
                >
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Dados do cliente
                  </div>
                  {visualizar.cliente_cnpj && (
                    <div className="text-[13px] text-slate-700">
                      CNPJ: <b className="font-mono-num">{visualizar.cliente_cnpj}</b>
                    </div>
                  )}
                  {visualizar.cliente_contato && (
                    <div className="text-[13px] text-slate-700">
                      Contato: <b>{visualizar.cliente_contato}</b>
                    </div>
                  )}
                  {visualizar.condicao_pagamento && (
                    <div className="text-[13px] text-slate-700">
                      Condição de pagamento: <b>{visualizar.condicao_pagamento}</b>
                    </div>
                  )}
                  {visualizar.prazo_entrega && (
                    <div className="text-[13px] text-slate-700">
                      Prazo de entrega: <b>{visualizar.prazo_entrega}</b>
                    </div>
                  )}
                  {visualizar.observacoes && (
                    <p className="mt-1 rounded-md bg-slate-50 px-2.5 py-1.5 text-[12px] leading-relaxed text-slate-600">
                      {visualizar.observacoes}
                    </p>
                  )}
                </div>
              )}

              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Itens ({visualizar.itens?.length || 0})
                </div>
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <Table data-testid="orcamento-visualizar-itens-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead className="text-right">Qtd.</TableHead>
                        <TableHead className="text-right">Desc.</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(visualizar.itens || []).map((it) => (
                        <TableRow key={it.produto_id}>
                          <TableCell>
                            <div className="text-[12.5px] font-medium text-slate-900">
                              {it.descricao_produto}
                            </div>
                            <div className="font-mono-num text-[11px] text-slate-500">
                              {it.codigo}
                            </div>
                          </TableCell>
                          <TableCell className="num text-right text-[12.5px]">
                            {it.quantidade}
                          </TableCell>
                          <TableCell className="num text-right text-[12.5px]">
                            {it.desconto_pct > 0 ? pct(it.desconto_pct) : "—"}
                          </TableCell>
                          <TableCell className="num text-right text-[12.5px] font-semibold text-slate-900">
                            {brl(it.total_item)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div
                className="space-y-1.5 rounded-lg bg-slate-50 px-3.5 py-3"
                data-testid="orcamento-visualizar-totais"
              >
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="text-slate-500">Total líquido</span>
                  <span className="num font-medium text-slate-800">
                    {brl(visualizar.totais?.total_liquido)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="text-slate-500">IPI</span>
                  <span className="num font-medium text-slate-800">
                    {brl(visualizar.totais?.total_ipi)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="text-slate-500">ICMS-ST / DIFAL</span>
                  <span className="num font-medium text-slate-800">
                    {brl(visualizar.totais?.total_st)}
                  </span>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-[14px] font-semibold text-slate-900">Total geral</span>
                  <span
                    className="num font-display text-[19px] font-bold text-primary"
                    data-testid="orcamento-visualizar-total-geral"
                  >
                    {brl(visualizar.totais?.total_geral)}
                  </span>
                </div>
              </div>

              <div
                className="space-y-2 rounded-lg border border-slate-200 p-3"
                data-testid="orcamento-visualizar-historico"
              >
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Histórico de alterações
                </div>
                {!visualizar.historico || visualizar.historico.length === 0 ? (
                  <p className="text-[12px] text-slate-500">Nenhuma alteração registrada ainda.</p>
                ) : (
                  <ul className="max-h-64 space-y-3 overflow-y-auto pr-1">
                    {[...visualizar.historico].reverse().map((ev) => (
                      <li
                        key={ev.id}
                        className="flex gap-2.5"
                        data-testid={`orcamento-historico-item-${ev.id}`}
                      >
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <div className="min-w-0">
                          <div className="text-[12.5px] text-slate-800">{descreverEvento(ev)}</div>
                          <div className="text-[11px] text-slate-500">
                            {dataHora(ev.data)} • {ev.usuario}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  className="h-11 flex-1"
                  onClick={() => {
                    const q = visualizar;
                    setVisualizar(null);
                    editarOrcamento(q);
                  }}
                  data-testid="orcamento-visualizar-editar-button"
                >
                  <Pencil className="mr-1.5 h-4 w-4" /> Editar
                </Button>
                <Button
                  variant="outline"
                  className="h-11"
                  onClick={() => abrirPdf(visualizar)}
                  data-testid="orcamento-visualizar-pdf-button"
                >
                  <FileDown className="mr-1.5 h-4 w-4" /> PDF
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog
        open={motivoDialog.aberto}
        onOpenChange={(o) => !o && setMotivoDialog({ aberto: false, quote: null, motivo: "" })}
      >
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Marcar orçamento como perdido</DialogTitle>
            <DialogDescription>
              Orçamento {motivoDialog.quote?.numero}. Informar o motivo ajuda a entender o funil de
              vendas (opcional).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="motivo-perda">Motivo da perda</Label>
            <Textarea
              id="motivo-perda"
              value={motivoDialog.motivo}
              onChange={(e) => setMotivoDialog((m) => ({ ...m, motivo: e.target.value }))}
              placeholder="Ex.: Preço acima do concorrente, prazo de entrega, etc."
              rows={3}
              data-testid="orcamentos-motivo-perda-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMotivoDialog({ aberto: false, quote: null, motivo: "" })}
              data-testid="orcamentos-motivo-perda-cancelar"
            >
              Cancelar
            </Button>
            <Button
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmarPerdido}
              data-testid="orcamentos-motivo-perda-confirmar"
            >
              Marcar como perdido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!excluir} onOpenChange={(o) => !o && setExcluir(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir orçamento {excluir?.numero}?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmarExclusao}
              data-testid="orcamentos-excluir-confirmar"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
